// src/common/hooks/useItemDataUpdater.ts
import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  toggleFavorite,
  addRating,
  removeRating,
  markAsViewed,
  clearRefreshFlag,
} from '../../store/slices/saleItemListSlice'
import {
  addPendingAction,
  removePendingAction,
  updateActionStatus,
  SyncActionType,
  SyncStatus,
} from '../../store/slices/syncSlice'
import { RatingValue } from '../types/enums'
import { selectCurrentUser } from '../../store/slices/currentUserSlice'
import {v4} from 'uuid'

export const useItemDataUpdater = () => {
  const dispatch = useDispatch()
  const currentUser = useSelector(selectCurrentUser)

  // Храним отслеженные действия в рамках сессии, чтобы не дублировать
  const trackedActionsRef = useRef<Set<string>>(new Set())

  // Генерация уникального ключа для действия
  const generateActionKey = (type: string, itemId: number, userId: number) => {
    return `${type}_${itemId}_${userId}`
  }

  // Вспомогательная функция для преобразования ошибок
  const getErrorMessage = useCallback((error: unknown): string => {
    if (error instanceof Error) return error.message
    if (typeof error === 'string') return error
    return 'Неизвестная ошибка'
  }, [])

  const updateFavorite = useCallback(
    (itemId: number, userId: number, addToPending: boolean = true, localId?: string) => {
      const actionLocalId = localId || v4()
      dispatch(toggleFavorite({ itemId, userId }))
      if (addToPending) {
        dispatch(
          addPendingAction({
            type: SyncActionType.FAVORITE,
            itemId,
            userId,
          }),
        )
      }
      return actionLocalId
    },
    [dispatch],
  )

  // === 2. ПРОСМОТРЫ ===
  const updateView = useCallback(
    (
      itemId: number,
      userId: number,
      viewedAt?: Date,
      addToPending: boolean = true,
      localId?: string,
    ) => {
      const actionKey = generateActionKey('view', itemId, userId)

      // ПРОВЕРКА: уже отслеживали в этой сессии?
      if (trackedActionsRef.current.has(actionKey)) {
        return undefined // Не дублируем
      }

      const actionLocalId = localId || 
      // 1. Оптимистичное обновление UI
      dispatch(markAsViewed({ itemId, userId }))

      // 2. Добавляем в pending actions
      if (addToPending) {
        dispatch(
          addPendingAction({
            type: SyncActionType.VIEW,
            itemId,
            userId,
            data: { viewedAt },
          }),
        )
      }

      // Помечаем как отслеженное
      trackedActionsRef.current.add(actionKey)

      return actionLocalId
    },
    [dispatch],
  )

  // === 3. ОЦЕНКИ ===
  const updateRating = useCallback(
    (
      itemId: number,
      userId: number,
      rating: RatingValue,
      comment?: string,
      files?: string[],
      addToPending: boolean = true,
      localId?: string,
    ) => {
      const actionLocalId =
        localId || `rate_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // 1. Оптимистичное обновление UI
      dispatch(
        addRating({
          itemId,
          userId,
          rating,
          comment,
          files,
        }),
      )

      // 2. Добавляем в pending actions
      if (addToPending) {
        dispatch(
          addPendingAction({
            type: SyncActionType.RATING,
            itemId,
            userId,
            data: { rating, comment, files },
          }),
        )
      }

      return actionLocalId
    },
    [dispatch],
  )

  // Удаление оценки
  const deleteRating = useCallback(
    (itemId: number, userId: number, addToPending: boolean = true) => {
      // 1. Оптимистичное обновление UI
      dispatch(removeRating({ itemId, userId }))

      // 2. Для удаления оценки можно не добавлять в pending
      // если есть DELETE API endpoint
    },
    [dispatch],
  )

  // === 4. УПРАВЛЕНИЕ PENDING ACTIONS ===

  const removeFromPending = useCallback(
    (localId: string) => {
      dispatch(removePendingAction(localId))
    },
    [dispatch],
  )

  const updatePendingStatus = useCallback(
    (localId: string, status: SyncStatus, error?: unknown) => {
      const errorMessage = error ? getErrorMessage(error) : undefined
      dispatch(updateActionStatus({ localId, status, error: errorMessage }))
    },
    [dispatch, getErrorMessage],
  )

  // === 5. ОЧИСТКА ФЛАГОВ ===

  const clearRefresh = useCallback(
    (itemId: number) => {
      dispatch(clearRefreshFlag(itemId))
    },
    [dispatch],
  )

  // === 6. ВСПОМОГАТЕЛЬНЫЕ ===

  const getCurrentUserId = useCallback(() => {
    return currentUser?.id
  }, [currentUser])

  // === 7. КОРОТКИЕ ВЕРСИИ (для текущего пользователя) ===

  const toggleFavoriteForCurrentUser = useCallback(
    (itemId: number, addToPending: boolean = true) => {
      const userId = currentUser?.id
      if (userId) {
        return updateFavorite(itemId, userId, addToPending)
      }
      return undefined
    },
    [currentUser, updateFavorite],
  )

  const addRatingForCurrentUser = useCallback(
    (
      itemId: number,
      rating: RatingValue,
      comment?: string,
      files?: string[],
      addToPending: boolean = true,
    ) => {
      const userId = currentUser?.id
      if (userId) {
        return updateRating(itemId, userId, rating, comment, files, addToPending)
      }
      return undefined
    },
    [currentUser, updateRating],
  )

  const markAsViewedForCurrentUser = useCallback(
    (itemId: number, addToPending: boolean = true) => {
      const userId = currentUser?.id
      if (userId) {
        return updateView(itemId, userId, undefined, addToPending)
      }
      return undefined
    },
    [currentUser, updateView],
  )

  // === 8. ОЧИСТКА ОТСЛЕЖЕННЫХ ДЕЙСТВИЙ (при логауте/перезагрузке) ===

  const clearTrackedActions = useCallback(() => {
    trackedActionsRef.current.clear()
  }, [])

  return {
    // Основные методы (с userId)
    updateFavorite,
    updateRating,
    deleteRating,
    updateView,

    // Управление pending actions
    removeFromPending,
    updatePendingStatus,

    // Очистка флагов
    clearRefresh,

    // Вспомогательные
    getCurrentUserId,
    clearTrackedActions,

    // Короткие версии (без userId)
    toggleFavorite: toggleFavoriteForCurrentUser,
    addRating: addRatingForCurrentUser,
    markAsViewed: markAsViewedForCurrentUser,
  }
}
